import { FilterX } from 'lucide-react';

import { getSessionLabel } from '@/lib/utils';
import { FilterType } from '@/stores/schedule-page';

import NoHobbyImage from '../../assets/no-hobby.svg';
import NoHolidayImage from '../../assets/no-holiday.svg';

const SESSION_LABEL = getSessionLabel();

interface CalendarEmptyStateProps {
  filter?: FilterType;
  selectedMonth?: string;
}

export const CalendarEmptyState = ({
  filter,
  selectedMonth,
}: CalendarEmptyStateProps) => {
  // Determine which image to show based on filter
  const getEmptyStateImage = () => {
    if (filter?.status === 'holiday') {
      return (
        <img
          src={NoHolidayImage}
          alt="No holidays found"
          className="w-48 h-48 object-contain"
        />
      );
    }
    if (filter?.variant === 'hobby_class') {
      return (
        <img
          src={NoHobbyImage}
          alt="No hobby classes found"
          className="w-48 h-48 object-contain"
        />
      );
    }
    // Default icon for general filter
    return (
      <div className="bg-magical-moonlight-l rounded-full p-6">
        <FilterX className="w-12 h-12 text-magical-moonlight-d2" />
      </div>
    );
  };

  // Determine title and description based on filter and month selection
  const getEmptyStateContent = () => {
    const hasMonthSelected = selectedMonth && selectedMonth !== 'Select all';

    // Holiday filter
    if (filter?.status === 'holiday') {
      if (hasMonthSelected) {
        return {
          title: 'No Holidays This Month 🏖️',
          description:
            'Check upcoming months to find any holidays that may coincide with your class days.',
        };
      }
      return {
        title: 'No Holidays! 🏖️',
        description:
          'No holidays are affecting your sessions — keep up your learning streak!',
      };
    }

    // Hobby class filter
    if (filter?.variant === 'hobby_class') {
      if (hasMonthSelected) {
        return {
          title: 'No Hobby Class cheduled for this month',
          description:
            "Your next Hobby Class will appear here once it's scheduled. Check back on Wednesday to see the updated schedule!",
        };
      }
      return {
        title: 'No Hobby Class Scheduled Yet',
        description:
          "Your next Hobby Class will appear here once it's scheduled. Check back on Wednesday to see the updated schedule!",
      };
    }

    // No filters and no lessons at all
    if (
      !filter?.status &&
      !filter?.variant &&
      (!selectedMonth || selectedMonth === 'Select all')
    ) {
      return {
        title: `No ${SESSION_LABEL.capitalize}s Found`,
        description: `You don't have any ${SESSION_LABEL.lowercase}s yet. Check back soon!`,
      };
    }

    // Default content for other filters
    return {
      title: `No ${SESSION_LABEL.capitalize}s Found`,
      description: `We couldn't find any ${SESSION_LABEL.lowercase}s matching your selected filters. Try adjusting your filters or clear them to see all ${SESSION_LABEL.lowercase}s.`,
    };
  };

  const { title, description } = getEmptyStateContent();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="flex flex-col items-center gap-6 max-w-md text-center">
        {/* Icon or Image */}
        {getEmptyStateImage()}

        {/* Message */}
        <div className="flex flex-col gap-2">
          <h3 className="text-xl font-semibold text-magical-moonlight-d2">
            {title}
          </h3>
          {description ? (
            <p className="text-magical-moonlight-d1">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
